import React, {
  createContext,
  FunctionComponent,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { CssBaseline, PaletteMode, ThemeProvider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { createAppTheme } from './createAppTheme';

export const COLOR_MODE_STORAGE_KEY = 'neo-color-mode';

interface ColorModeContextValue {
  mode: PaletteMode;
  toggleColorMode: () => void;
}

const ColorModeContext = createContext<ColorModeContextValue | undefined>(
  undefined
);

const getInitialMode = (): PaletteMode => {
  try {
    return localStorage.getItem(COLOR_MODE_STORAGE_KEY) === 'dark'
      ? 'dark'
      : 'light';
  } catch {
    return 'light';
  }
};

export const AppThemeProvider: FunctionComponent = ({ children }) => {
  const [mode, setMode] = useState<PaletteMode>(getInitialMode);
  const { i18n } = useTranslation();
  const theme = useMemo(
    () => createAppTheme(mode, i18n.language),
    [mode, i18n.language]
  );
  const colorMode = useMemo(
    () => ({
      mode,
      toggleColorMode: () => {
        setMode((current) => (current === 'light' ? 'dark' : 'light'));
      },
    }),
    [mode]
  );

  useEffect(() => {
    try {
      localStorage.setItem(COLOR_MODE_STORAGE_KEY, mode);
    } catch {
      // Theme switching also works when browser storage is unavailable.
    }
  }, [mode]);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline enableColorScheme />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};

export const useAppTheme = () => {
  const context = useContext(ColorModeContext);
  if (!context) {
    throw new Error('useAppTheme must be used within AppThemeProvider');
  }
  return context;
};
